import angular from 'angular';
import {
  PRODUCT_LABEL_CHANGED,
  PRODUCT_ADDED,
  PRODUCT_EDITED,
  PRODUCT_SIGNATURE_ADDED,
  PRODUCT_COPIED,
  ADMINISTRATION_ADDED,
  ADMINISTRATION_EDITED,
  ADMINISTRATION_DELETED,
  PRODUCT_INGREDIENT_ADDED,
  PRODUCT_INGREDIENT_EDITED,
  PRODUCT_INGREDIENT_DELETED
} from 'common/messages';
import templateUrl from './product.html';

export default {
  templateUrl,
  bindings: {
    product: '<',
    productLabel: '<',
    animalTypes: '<',
    categories: '<',
    frequencies: '<',
    administrationUnits: '<',
    weightUnits: '<'
  },
  controller: class ProductComponent {
    constructor($mdDialog, $scope, $state, ameLightbox,
      AuthService, ArrayService, IngredientService,
      LoadingService, ProductService, ToastService, Upload) {
      'ngInject';

      Object.assign(this, {
        $mdDialog,
        $scope,
        $state,
        ameLightbox,
        ArrayService,
        AuthService,
        IngredientService,
        LoadingService,
        ProductService,
        ToastService,
        Upload
      });
    }

    $onInit() {
      // Make sure we're on the right page for the product type
      if (this.product.IsTreat) {
        this.$state.go('treatProductEdit', { id: this.product.ProductId }, { reload: true });
        return;
      }

      this.product.tblDoses = this.product.tblDoses || [];
      this.product.tblUsages = this.product.tblUsages || [];
      this.selectedLoadingAdministrations = [];
      this.selectedMaintenanceAdministrations = [];
      this.product.tblUsages =
        this.ProductService.transformUsages(this.product.ProductId,
          this.product.DateIntroduced,
          this.product.tblUsages);

      this.selectedIngredients = [];
      this.labelChanged = false;
      this.duplicateAdministration = false;
      this.product.tblProduct_UserIngredient = this.product.tblProduct_UserIngredient || [];

      this.maxDate = new Date();

      this.copyAreas = [
        'Product Information',
        'Administrations',
        'Product Ingredients'
      ]; // CG 11/15/2023 removed Usage Data from this list. We should not copy Usage between products.
    }

    onLoadingDoseRequiredChanged($event) {
      if ($event && !$event.loadingDoseRequired) {
        this.product.tblDoses = this.product.tblDoses.filter(d => d.Isloading === false);
      }
    }

    signOff() {
      const scope = angular.extend(this.$scope.$new(true), {
        productId: this.product.ProductId,
        units: this.unitPerContainer,
        chosenUnits: this.chosenUnits,
        isTreat: false,
        onSignedOff: $event => this.onSignedOff($event)
      });

      if (this.product.tblProduct_UserIngredient.length === 0) {
        this.ToastService.showWarning('Must specify Ingredients before signing off');
      } else if (this.product.tblDoses.length === 0) {
        this.ToastService.showWarning('Must specify Directions for Use before signing off');
      } else {
        this.$mdDialog.show({
          clickOutsideToClose: true,
          fullscreen: true,
          scope,
          template: `
            <md-dialog class="flex-50">
              <md-dialog-content>
                <dialog-close/>
                <sign-off product-id="productId"
                  units="units" chosen-units="chosenUnits"
                  is-treat="false"
                  on-signed-off="onSignedOff($event)">
                </sign-off>
              </md-dialog-content>
            </md-dialog>
            `
        });
      }
    }

    saveProduct() {
      let promise;
      if (this.product.ProductId) {
        promise = this.ProductService.update(this.product);
      } else {
        promise = this.ProductService.create(this.product);
      }

      return promise.then((r) => {
        this.product.ProductId = this.product.ProductId || (r.data.Id);
      }).then(() => this.saveProductLabel());
    }

    onSignedOff({ signature }) {
      if (signature) {
        this.LoadingService.start();
        this.saveProduct()
          .then(() => this.ProductService.signOff(this.product.ProductId, signature))
          .then(() => {
            this.$mdDialog.hide();
            this.ToastService.showSuccess(PRODUCT_SIGNATURE_ADDED);
            this.$state.go('productEdit', { id: this.product.ProductId }, { reload: true });
          }).catch(error => this.ToastService.showError(error))
          .finally(() => this.LoadingService.stop());
      }
    }

    showCopy() {
      const scope = angular.extend(this.$scope.$new(true), {
        copyAreas: this.copyAreas,
        isTreat: false,
        onCopied: $event => this.onCopied($event)
      });

      this.$mdDialog.show({
        clickOutsideToClose: true,
        fullscreen: true,
        scope,
        template: `
          <md-dialog class="flex-50">
            <md-dialog-content>
              <dialog-close/>
              <product-copy copy-areas="copyAreas" is-treat="isTreat"
                on-copied="onCopied($event)">
              </product-copy>
            </md-dialog-content>
          </md-dialog>
          `
      });
    }

    onCopied($event) {
      const selectedCopyAreas = $event.copyAreas;

      this.ProductService.getProduct($event.product.ProductId)
        .then((p) => {
          const copyFromProduct = p.data;

          if (selectedCopyAreas && selectedCopyAreas.length) {
            this.product = {
              ...this.ProductService.copyProduct(this.product, copyFromProduct, selectedCopyAreas)
            };
          }

          this.ToastService.showSuccess(PRODUCT_COPIED);
          this.$mdDialog.hide();
        }).catch(error => this.ToastService.showError(error));
    }

    dateFirstSoldChanged($event) {
      this.product.tblUsages =
        this.ProductService.transformUsages(this.product.ProductId,
          $event.dateFirstSold, this.product.tblUsages);
    }

    get unitsDisplay() {
      return this.ProductService.getUnitsDisplay(this.product);
    }

    get unitPerContainer() {
      return this.product.UnitPerContainer;
    }

    get chosenUnits() {
      return this.product.ProductForm;
    }

    saveChanges() {
      const saveMessage = this.product.ProductId ? PRODUCT_EDITED : PRODUCT_ADDED;
      this.LoadingService.start();
      this.saveProduct().then(() => {
        this.ToastService.showSuccess(saveMessage);
        this.$state.go('productEdit', { id: this.product.ProductId }, { reload: true });
      }).catch(error => this.ToastService.showError(error))
        .finally(() => this.LoadingService.stop());
    }

    saveProductLabel() {
      if (this.productLabel && this.productLabel instanceof File) {
        return this.ProductService
          .uploadLabel(this.product.ProductId, this.productLabel);
      }

      return Promise.resolve(true);
    }

    setLabelImage({ productLabel }) {
      this.productLabel = productLabel;

      if (productLabel) {
        this.ToastService.showSuccess(PRODUCT_LABEL_CHANGED);
      }
    }

    onAdministrationAdded({ administration, shouldClose }) {
      if (administration) {
        this.product.tblDoses =
          this.ArrayService.add(this.product.tblDoses, administration);

        this.ToastService.showSuccess(ADMINISTRATION_ADDED);
        if (shouldClose) {
          this.$mdDialog.hide();
        }
      }
    }

    onAdministrationEdited({ administration, index }) {
      if (administration && index !== null) {
        this.product.tblDoses =
          this.ArrayService.update(this.product.tblDoses, administration, index);

        this.ToastService.showSuccess(ADMINISTRATION_EDITED);
        this.$mdDialog.hide();
        this.selectedLoadingAdministrations = [];
        this.selectedMaintenanceAdministrations = [];
      }
    }

    maintenanceAdministrationAdd() {
      const scope = angular.extend(this.$scope.$new(true), {
        administration: undefined,
        administrations: angular.copy(this.product.tblDoses),
        units: this.unitPerContainer,
        animalTypes: this.animalTypes,
        frequencies: this.frequencies,
        unitsDisplay: this.unitsDisplay,
        administrationChanged: $event => this.onAdministrationAdded($event)
      });

      this.$mdDialog.show({
        clickOutsideToClose: true,
        fullscreen: true,
        scope,
        template: `
          <md-dialog class="flex-50">
            <md-dialog-content>
              <dialog-close/>
              <maintenance-admin-form
                administration="administration"
                administrations="administrations"
                units="units"
                animal-types="animalTypes"
                frequencies="frequencies"
                units-display="unitsDisplay"
                submit-title="Add Administration"
                show-additional-button="true"
                administration-changed="administrationChanged($event)">
              </maintenance-admin-form>
            </md-dialog-content>
          </md-dialog>
          `
      });
    }

    maintenanceAdministrationEdit(administration) {
      const scope = angular.extend(this.$scope.$new(true), {
        administration: angular.copy(administration),
        administrations: angular.copy(this.product.tblDoses),
        units: this.unitPerContainer,
        animalTypes: this.animalTypes,
        frequencies: this.frequencies,
        unitsDisplay: this.unitsDisplay,
        index: this.product.tblDoses.indexOf(administration),
        administrationChanged: $event => this.onAdministrationEdited($event)
      });

      this.$mdDialog.show({
        clickOutsideToClose: true,
        fullscreen: true,
        scope,
        template: `
          <md-dialog class="flex-50">
            <md-dialog-content>
              <dialog-close/>
              <maintenance-admin-form
                administration="administration"
                administrations="administrations"
                units="units"
                animal-types="animalTypes"
                frequencies="frequencies"
                units-display="unitsDisplay"
                submit-title="Save Changes"
                administration-changed="administrationChanged($event)"
                index="index">
              </maintenance-admin-form>
            </md-dialog-content>
          </md-dialog>
          `
      });
    }

    maintenanceAdministrationDelete(administrations) {
      const confirm = this.$mdDialog.confirm()
        .title('Are you sure?')
        .textContent('Once deleted, the selected maintenance administration(s) cannot be recovered!')
        .ok('Delete')
        .cancel('Cancel');

      this.$mdDialog.show(confirm).then(() => {
        try {
          for (let i = administrations.length - 1; i >= 0; i -= 1) {
            this.product.tblDoses =
              this.ArrayService.remove(this.product.tblDoses, administrations[i]);
          }

          this.ToastService.showSuccess(ADMINISTRATION_DELETED);
          this.selectedMaintenanceAdministrations = [];
        } catch (e) {
          this.ToastService.showError();
        }
      });
    }

    loadingAdministrationAdd() {
      const scope = angular.extend(this.$scope.$new(true), {
        administration: undefined,
        administrations: angular.copy(this.product.tblDoses),
        animalTypes: this.animalTypes,
        frequencies: this.frequencies,
        unitsDisplay: this.unitsDisplay,
        administrationChanged: $event => this.onAdministrationAdded($event)
      });

      this.$mdDialog.show({
        clickOutsideToClose: true,
        fullscreen: true,
        scope,
        template: `
          <md-dialog class="flex-50">
            <md-dialog-content>
              <dialog-close/>
              <loading-admin-form
                administration="administration"
                administrations="administrations"
                animal-types="animalTypes"
                frequencies="frequencies"
                units-display="unitsDisplay"
                submit-title="Add Administration"
                show-additional-button="true"
                administration-changed="administrationChanged($event)">
              </loading-admin-form>
            </md-dialog-content>
          </md-dialog>
          `
      });
    }

    loadingAdministrationEdit(administration) {
      const scope = angular.extend(this.$scope.$new(true), {
        administration: angular.copy(administration),
        administrations: angular.copy(this.product.tblDoses),
        animalTypes: this.animalTypes,
        frequencies: this.frequencies,
        unitsDisplay: this.unitsDisplay,
        index: this.product.tblDoses.indexOf(administration),
        administrationChanged: $event => this.onAdministrationEdited($event)
      });

      this.$mdDialog.show({
        clickOutsideToClose: true,
        fullscreen: true,
        scope,
        template: `
          <md-dialog class="flex-50">
            <md-dialog-content>
              <dialog-close/>
              <loading-admin-form
                administration="administration"
                administrations="administrations"
                animal-types="animalTypes"
                frequencies="frequencies"
                units-display="unitsDisplay"
                submit-title="Save Changes"
                index="index"
                administration-changed="administrationChanged($event)">
              </loading-admin-form>
            </md-dialog-content>
          </md-dialog>
          `
      });
    }

    loadingAdministrationDelete(administrations) {
      const confirm = this.$mdDialog.confirm()
        .title('Are you sure?')
        .textContent('Once deleted, the selected loading administration(s) cannot be recovered!')
        .ok('Delete')
        .cancel('Cancel');

      this.$mdDialog.show(confirm).then(() => {
        try {
          for (let i = administrations.length - 1; i >= 0; i -= 1) {
            this.product.tblDoses =
              this.ArrayService.remove(this.product.tblDoses, administrations[i]);
          }

          this.ToastService.showSuccess(ADMINISTRATION_DELETED);
          this.selectedLoadingAdministrations = [];
        } catch (e) {
          this.ToastService.showError();
        }
      });
    }

    onIngredientAdded({ ingredient, shouldClose }) {
      if (ingredient) {
        this.product.tblProduct_UserIngredient =
          this.ArrayService.add(this.product.tblProduct_UserIngredient, ingredient);
        this.ToastService.showSuccess(PRODUCT_INGREDIENT_ADDED);
        if (shouldClose) {
          this.$mdDialog.hide();
        }
      }
    }

    onIngredientEdited({ ingredient, index }) {
      if (ingredient && index !== null) {
        this.product.tblProduct_UserIngredient =
          this.ArrayService.update(this.product.tblProduct_UserIngredient, ingredient, index);
        this.ToastService.showSuccess(PRODUCT_INGREDIENT_EDITED);
        this.$mdDialog.hide();
        this.selectedIngredients = [];
      }
    }

    ingredientAdd() {
      const scope = angular.extend(this.$scope.$new(true), {
        companyId: this.product.MemberId,
        ingredients: angular.copy(this.product.tblProduct_UserIngredient),
        unitsDisplay: this.unitsDisplay,
        weightUnits: this.weightUnits,
        ingredientChanged: $event => this.onIngredientAdded($event)
      });

      this.$mdDialog.show({
        clickOutsideToClose: true,
        fullscreen: true,
        scope,
        template: `
          <md-dialog class="flex-50">
            <md-dialog-content>
              <dialog-close/>
              <product-ingredient-form
                ingredients="ingredients"
                weight-units="weightUnits"
                units-display="unitsDisplay"
                submit-title="Add Ingredient"
                show-additional-button="true"
                company-id="companyId"
                ingredient-changed="ingredientChanged($event)">
              </product-ingredient-form>
            </md-dialog-content>
          </md-dialog>
          `
      });
    }

    ingredientEdit(ingredient) {
      const scope = angular.extend(this.$scope.$new(true), {
        ingredient: angular.copy(ingredient),
        ingredients: angular.copy(this.product.tblProduct_UserIngredient),
        companyId: this.product.MemberId,
        index: this.product.tblProduct_UserIngredient.indexOf(ingredient),
        unitsDisplay: this.unitsDisplay,
        weightUnits: this.weightUnits,
        ingredientChanged: $event => this.onIngredientEdited($event)
      });

      this.$mdDialog.show({
        clickOutsideToClose: true,
        fullscreen: true,
        scope,
        template: `
          <md-dialog class="flex-50">
            <md-dialog-content>
              <dialog-close/>
              <product-ingredient-form
                ingredient="ingredient"
                ingredients="ingredients"
                weight-units="weightUnits"
                units-display="unitsDisplay"
                submit-title="Save Changes"
                company-id="companyId"
                ingredient-changed="ingredientChanged($event)"
                index="index">
              </product-ingredient-form>
            </md-dialog-content>
          </md-dialog>
          `
      });
    }

    ingredientDelete(ingredients) {
      const confirm = this.$mdDialog.confirm()
        .title('Are you sure?')
        .textContent('Once deleted, the selected ingredient(s) cannot be recovered!')
        .ok('Delete')
        .cancel('Cancel');

      this.$mdDialog.show(confirm).then(() => {
        try {
          for (let i = ingredients.length - 1; i >= 0; i -= 1) {
            this.product.tblProduct_UserIngredient =
              this.ArrayService.remove(this.product.tblProduct_UserIngredient, ingredients[i]);
          }

          this.ToastService.showSuccess(PRODUCT_INGREDIENT_DELETED);
          this.selectedIngredients = [];
        } catch (e) {
          this.ToastService.showError();
        }
      });
    }

    get labelValid() {
      return this.productLabel || this.product.FileLabelName;
    }

    get shouldBeLocked() {
      if (this.AuthService.isSysAdmin) {
        return false;
      }

      return this.product.Signature && this.product.Signature.length > 0;
    }
  }
};



// WEBPACK FOOTER //
// ./components/app/product/product.component.js